import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa6';
import { FiShield, FiSettings } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { toast } from 'react-toastify';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import PageFooter from '../../molecules/PageFooter';
import TextEdit from '../../molecules/TextEdit';
import VerificationConfigPageForm from './VerificationConfigPageForm';
import BatchStatusBadge from './shared/BatchStatusBadge';
import CriteriaConfigPanelEdit from './CriteriaConfigPanelEdit';
import { formatCurrency, formatCpfCnpj, getIntensityColor } from '../../../utils/collateralFormatters';
import type { AnalysisIntensity } from '../../../types/Collateral';
import useVerificationConfigPageList, { type FilterKey } from '../../../hooks/useVerificationConfigPageList';

const FILTER_SECTIONS: { key: FilterKey; title: string }[] = [
    { key: 'guaranteeStatus', title: 'STATUS GARANTIA' },
    { key: 'intensity', title: 'INTENSIDADE' },
    { key: 'operationType', title: 'TIPO OPERAÇÃO' },
    { key: 'obligationType', title: 'OBRIGAÇÃO' },
];

const columns = [
    { id: 'op', title: 'Operação', field: 'operationCode', canSort: true, width: '90px', cssClass: 'sticky-left z-20' },
    { id: 'assignor', title: 'Cedente', field: 'assignor', canSort: true, width: '300px', cssClass: 'sticky-left-1 z-20' },
    { id: 'issueDate', title: 'Emissão', field: 'issueDate', canSort: true, width: '80px', cssClass: 'text-center' },
    { id: 'obligation', title: 'Obrigação', field: 'obligation', canSort: false, width: '100px', cssClass: 'text-center' },
    { id: 'totalAmount', title: 'Valor Total', field: 'totalAmount', canSort: true, width: '120px', cssClass: 'text-right' },
    { id: 'valuePct', title: '% Valor', field: 'totalAmount', canSort: false, width: '70px', cssClass: 'text-center' },
    { id: 'receivables', title: 'Recebíveis', field: 'totalReceivables', canSort: true, width: '80px', cssClass: 'text-center' },
    { id: 'validatedPct', title: '% Receb.', field: 'validatedPercentage', canSort: true, width: '70px', cssClass: 'text-center' },
    { id: 'intensity', title: 'Intensidade', field: 'analysisIntensity', canSort: false, width: '90px', cssClass: 'text-center' },
    { id: 'status', title: 'Status', field: 'guaranteeStatus', canSort: false, width: '140px', cssClass: 'text-center' },
    { id: 'actions', title: '', field: 'actions', canSort: false, width: '30px', cssClass: 'text-center' },
];

const VerificationConfigPageList = () => {
    const {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        apiInfo, filterCounts, filters,
        showNewForm, setShowNewForm,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        hasActiveFilter, refresh,
        setFilter, clearFilters, handleRowClick,
    } = useVerificationConfigPageList();

    const [editingConfig, setEditingConfig] = useState<any>(null);

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={clearFilters}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!hasActiveFilter
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                {total}
                            </span>
                        </div>
                    </button>

                    {FILTER_SECTIONS.map(section => {
                        const counts = filterCounts[section.key] || {};
                        if (Object.keys(counts).length === 0) return null;
                        return (
                            <div key={section.key} className="border-t border-gray-200 dark:border-slate-700 pt-3">
                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">{section.title}</div>
                                <div className="space-y-1">
                                    {Object.entries(counts).sort(([a], [b]) => a.localeCompare(b)).map(([key, data]: [string, any]) => (
                                        <button key={key} onClick={() => setFilter(section.key, key)}
                                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${filters[section.key] === key
                                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                            <div className="flex items-center justify-between">
                                                <span className="truncate">{data.label || key}</span>
                                                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{data.count || data}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loading />}

            {!showNewForm && (
                <button onClick={() => setShowNewForm(true)}
                    className="fixed top-[69px] right-[10px] w-10 h-10 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 z-50 flex items-center justify-center border-2 border-white dark:border-slate-800 bg-blue-600 hover:bg-blue-700 text-white"
                    title="Nova Verificação de Recebíveis">
                    <FaPlus size={14} />
                </button>
            )}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={showNewForm}
                onCloseRightPanel={() => setShowNewForm(false)}
                rightPanel={showNewForm ? (
                    <div className="h-full p-6 overflow-auto">
                        <VerificationConfigPageForm onClose={() => setShowNewForm(false)} onCreated={() => { setShowNewForm(false); refresh(); toast.success('Análise criada'); }} />
                    </div>
                ) : null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Verificação de Recebíveis</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-80">
                                    <TextEdit label="Buscar operação, cedente, CNPJ, sacado..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-x-auto overflow-y-auto [&_table]:table-fixed">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhuma análise encontrada</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-medium dark:text-white sticky-left max-w-[90px]" title={row.operationCode}>
                                            <div className="truncate">{row.operationCode}</div>
                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 dark:bg-slate-600 dark:text-slate-200">{row.operationType}</span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white sticky-left-1 max-w-[250px]" title={row.assignor}>
                                            <div className="truncate">{row.assignor}</div>
                                            <div className="text-xs text-gray-400 dark:text-gray-500">{formatCpfCnpj(row.assignorTaxId)}</div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-sm dark:text-gray-300">{row.issueDate}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-xs font-medium dark:text-gray-300 uppercase">{row.obligation}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-right text-sm dark:text-white">
                                            <div>{formatCurrency(row.totalAmount || 0)}</div>
                                            <div className="text-[10px] text-green-600">{formatCurrency(row.validatedAmount || 0)}</div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-xs dark:text-white">
                                            <span className={`font-medium ${row.totalAmount > 0 && row.validatedAmount >= row.totalAmount ? 'text-green-600' : ''}`}>{row.totalAmount > 0 ? Math.round((row.validatedAmount || 0) / row.totalAmount * 100) : 0}%</span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-xs dark:text-white">
                                            <span>{row.validatedReceivables || 0}/{row.totalReceivables || 0}</span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-xs dark:text-white">
                                            <span className={`font-medium ${(row.validatedPercentage || 0) >= 100 ? 'text-green-600' : ''}`}>{row.validatedPercentage || 0}%</span>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            {row.analysisIntensity && row.analysisIntensity !== '—'
                                                ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getIntensityColor(row.analysisIntensity as AnalysisIntensity)}`}>{row.analysisIntensity}</span>
                                                : <span className="text-xs text-gray-400">{row.analysisIntensity}</span>}
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <div className="flex flex-col items-center gap-0.5">
                                                <BatchStatusBadge status={row.guaranteeStatus} statusFormatted={row.guaranteeStatusFormatted || row.guaranteeStatus} />
                                                <BatchStatusBadge status={row.validationStatus} statusFormatted={row.validationStatusFormatted || row.validationStatus} />
                                            </div>
                                        </td>
                                        <td className="py-2 px-1 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setEditingConfig(row); }}
                                                title="Alterar critérios de validação"
                                                className="p-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-600">
                                                <FiSettings size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            <Outlet context={{ onDataChange: refresh }} />

            {editingConfig && (
                <CriteriaConfigPanelEdit
                    configUuid={editingConfig.id}
                    validationConfig={Object.fromEntries(
                        (editingConfig.criteria || []).map((c: any) => {
                            const keyMap: Record<string, string> = {
                                XML: 'validation_xml', INVOICE_PDF: 'validation_invoice_pdf',
                                RECEIPT_STUB: 'validation_receipt_stub', DRAWEE_CONFIRMATION: 'validation_contact',
                                SEFAZ: 'validation_sefaz', ACCEPTED_BILL: 'validation_accepted_bill',
                                PURCHASE_CONTRACT: 'validation_purchase_contract',
                            };
                            return [keyMap[c.criteriaType] || c.criteriaType, c.timing];
                        })
                    )}
                    operationSummary={{ operationCode: editingConfig.operationCode || '', assignorName: editingConfig.assignor || '', totalAmount: editingConfig.totalAmount || 0, totalReceivables: editingConfig.totalReceivables || 0 }}
                    operationType={editingConfig.operationType || ''}
                    obligationType={editingConfig.obligationType || ''}
                    traderName={editingConfig.traderName || ''}
                    analysisCriteria={editingConfig.analysisMode === 'SAMPLE' ? 'Amostragem' : 'Completa'}
                    sampledTitles={editingConfig.sampleCount || 1}
                    hasDraweeInProgress={editingConfig.validationStatus === 'Parcialmente validada' || editingConfig.validationStatus === 'Em validação'}
                    onClose={() => setEditingConfig(null)}
                    onSaved={() => { setEditingConfig(null); refresh(); }}
                />
            )}
        </>
    );
};

export default VerificationConfigPageList;
